import { ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Shade add-to-cart feedback. A single delegated listener on the theme's
 * `cart:update` event turns the stock add-to-cart into a bespoke terminal
 * "committed" beat: the triggering button runs a one-shot confirmation pulse
 * (or a shake on error) and the header cart count bumps like a receipt.
 *
 * Motion-neutral by design but visually a motion layer, so the whole thing is
 * gated behind prefers-reduced-motion: reduced-motion users keep the theme's
 * native static added state + aria-live announcement. Transform/opacity only,
 * one document-level listener, idempotent, self-instantiating (no liquid edit).
 *
 * CartUpdateEvent detail shape (verified in assets/events.js): the source
 * element is detail.sourceId and the failure flag is the NESTED detail.data
 * .didError (not a flat detail.didError).
 */
class ShadeCart extends HTMLElement {
  connectedCallback() {
    if (prefersReducedMotion()) return;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail || {};
    const data = detail.data || {};
    const state = data.didError ? 'error' : 'added';

    // Resolve the add-to-cart button that triggered the update.
    const source = detail.sourceId ? document.getElementById(detail.sourceId) : null;
    let button = null;
    if (source) {
      button = source.matches('button')
        ? source
        : source.querySelector('button[name="add"], .add-to-cart-button, .quick-add__submit');
    }
    if (button) this.#pulse(button, state);

    // Header cart count bubble: receipt bounce on success only.
    if (!data.didError) {
      const bubble = document.querySelector('.cart-bubble');
      if (bubble) this.#pulse(bubble, 'bump');
    }
  };

  /**
   * Re-triggerable one-shot: strip the attribute, force a reflow so the CSS
   * keyframe restarts even on rapid repeat adds, then set the new state.
   * @param {HTMLElement} el
   * @param {'added'|'error'|'bump'} state
   */
  #pulse(el, state) {
    el.removeAttribute('data-shade-atc');
    el.removeAttribute('data-shade-bump');
    void el.offsetWidth; // reflow
    if (state === 'bump') el.dataset.shadeBump = 'on';
    else el.dataset.shadeAtc = state;
  }
}

if (!customElements.get('shade-cart')) {
  customElements.define('shade-cart', ShadeCart);
}

if (!document.querySelector('shade-cart')) {
  document.body.appendChild(document.createElement('shade-cart'));
}
